import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, map, skip } from 'rxjs/operators';

import { TimelineService } from '@models/milestone-category/milestone/timeline.service';
import { PatientCurveQuery } from '@models/patient-curve/patient-curve.query';
import { SiteCurveService } from '@models/site-curve/site-curve.service';

import { EditableListDropdownItem } from '@shared/components/editable-list-dropdown/editable-list-dropdown-item.model';
import { GqlService, PermissionType } from '@shared/services/gql.service';
import { AuthService } from '@shared/store/auth/auth.service';

import { ForecastTableGridCategoryToggleService } from './forecast-table-grid-category-toggle.service';
import { ForecastTableGridCategoryService } from './forecast-table-grid-category.service';
import { ForecastTableGridMethodService } from './forecast-table-grid-method.service';
import { ForecastTableGridPeriodService } from './forecast-table-grid-period.service';
import {
  ForecastTableGridDataInterface,
  ForecastTableGridSiteCurveInterface,
  ForecastTableGridTimelineMilestoneInterface,
  ForecastTableGridTimelinePhaseInterface,
} from '../models/forecast-table-grid.model';

@Injectable({
  providedIn: 'root',
})
export class ForecastTableGridService {
  private authService = inject(AuthService);
  private categoryService = inject(ForecastTableGridCategoryService);
  private gqlService = inject(GqlService);
  private methodService = inject(ForecastTableGridMethodService);
  private patientCurveQuery = inject(PatientCurveQuery);
  private periodService = inject(ForecastTableGridPeriodService);
  private siteCurveService = inject(SiteCurveService);
  private timelineService = inject(TimelineService);
  private toggleService = inject(ForecastTableGridCategoryToggleService);

  getAuthorization(): Observable<boolean> {
    return this.authService.isAuthorized$({
      permissions: [PermissionType.PERMISSION_MODIFY_FORECAST_METHODOLOGY],
      sysAdminsOnly: false,
    });
  }

  getChangeDiscountChanges(
    changeDiscountTotalAmountValueChanges: Observable<unknown>,
    changeDiscountTotalPercentValueChanges: Observable<unknown>
  ) {
    return combineLatest([
      changeDiscountTotalAmountValueChanges,
      changeDiscountTotalPercentValueChanges,
    ]).pipe(skip(2), debounceTime(1000));
  }

  getCustomCurves() {
    return this.gqlService.listDriverCustomGroupsWithData$().pipe(
      map(({ data }) => {
        return data || [];
      })
    );
  }

  getPatientCurves(): Observable<EditableListDropdownItem[]> {
    return this.patientCurveQuery
      .selectAll()
      .pipe(map(this.methodService.parsePatientCurves.bind(this)));
  }

  getRowData(): Observable<[ForecastTableGridDataInterface[], ForecastTableGridDataInterface[]]> {
    return combineLatest([
      this.categoryService.getServiceCategories(),
      this.categoryService.getCostCategories(),
      this.toggleService.getToggleStatus(),
    ]).pipe(map(this.categoryService.filterCategoryView.bind(this)));
  }

  getSiteCurves(): Observable<ForecastTableGridSiteCurveInterface[]> {
    return this.siteCurveService.get().pipe(map(this.methodService.parseSiteCurves.bind(this)));
  }

  getTimelineItems(): Observable<
    [ForecastTableGridTimelinePhaseInterface[], ForecastTableGridTimelineMilestoneInterface[]]
  > {
    return this.timelineService
      .getTimelineItems()
      .pipe(map(this.periodService.parseTimelineItems.bind(this.periodService)));
  }
}
